import { CaptionStyle, TelopPosition, TelopSize } from '../../services/api/songAdService';

/**
 * テロップ4プリセット（spec §16.3）。mockup.html の `.cap-simple/.cap-pop/.cap-impact/.cap-premium`
 * のCSS（色・縁取り・影・フォントウェイト）をそのままReactインラインスタイルに移植したもの。
 * ffmpegレンダラー側のASSプリセット（B5）もこの配色・ウェイトを正として実装する。
 */
export const CAPTION_STYLE_CSS: Record<CaptionStyle, React.CSSProperties> = {
  simple: {
    color: '#fff',
    fontWeight: 700,
    textShadow: '0 1px 10px rgba(0,0,0,.55), 0 0 2px rgba(0,0,0,.35)',
  },
  pop: {
    color: '#1D1927',
    fontWeight: 800,
    background: 'rgba(255,255,255,.92)',
    borderRadius: 9999,
    padding: '.28em .9em',
    boxShadow: '0 2px 10px rgba(0,0,0,.18)',
  },
  impact: {
    color: '#FFE44D',
    fontWeight: 900,
    letterSpacing: '.02em',
    textShadow:
      '-2px -2px 0 #1D1927, 2px -2px 0 #1D1927, -2px 2px 0 #1D1927, 2px 2px 0 #1D1927, 0 4px 14px rgba(0,0,0,.4)',
  },
  premium: {
    color: '#fff',
    fontFamily: "'Noto Serif JP', serif",
    fontWeight: 600,
    letterSpacing: '.14em',
    textShadow: '0 1px 8px rgba(0,0,0,.5)',
  },
};

export const CAPTION_STYLE_OPTIONS: { key: CaptionStyle; label: string }[] = [
  { key: 'simple', label: 'シンプル' },
  { key: 'pop', label: 'ポップ' },
  { key: 'impact', label: 'インパクト' },
  { key: 'premium', label: 'プレミアム' },
];

export const CAPTION_POSITION_CSS: Record<TelopPosition, React.CSSProperties> = {
  top: { top: '7%' },
  middle: { top: '50%', transform: 'translateY(-50%)' },
  bottom: { bottom: '9%' },
};

export const CAPTION_POSITION_OPTIONS: { key: TelopPosition; label: string }[] = [
  { key: 'top', label: '上' },
  { key: 'middle', label: '中央' },
  { key: 'bottom', label: '下' },
];

/**
 * テロップのフォントサイズ計算式（v6 F3/r）。backendの `AssSubtitleBuilder`（B5で確定）と
 * 完全に同一の式に統一する: `min(width*WIDTH_BUDGET/MAX_CHARS_PER_LINE, height*HEIGHT_BUDGET) × SIZE_RATIO[size]`。
 *
 * 旧実装は `height * 比率`（frame高さのみ）で計算していたため、9:16ではプレビューが書き出し
 * （ASS）より約17%大きく、16:9では逆にASSの方が約67%大きいというズレが生じていた
 * （GOAL.md 要件分析 r）。`useVideoConfig()` が返す `width` も使うことでこのズレを解消する。
 *
 * 定数値は `AssSubtitleBuilder.php`（B5実行メモ）と同値。値を変えるときは両方揃えること。
 * 既定サイズは v6 F3 で 中(md) → 大(lg) に変更（9:16で 46px → 56px、約+20%）。
 */
export const CAPTION_MAX_CHARS_PER_LINE = 11;
export const CAPTION_WIDTH_BUDGET = 0.86;
export const CAPTION_HEIGHT_BUDGET = 0.085;

/** テロップサイズ（基準サイズに対する比）。lgが「1行11文字が画面幅に収まる最大」になるよう決めてある */
export const CAPTION_SIZE_RATIO: Record<TelopSize, number> = {
  sm: 0.71,
  md: 0.83,
  lg: 1.0,
};

/**
 * テロップのフォントサイズ（px）を算出する。ASS（書き出し）側 `AssSubtitleBuilder`（B5実行メモ）
 * と完全に同じ2段floorで丸める: **先に base を floor してから size 比率を掛けて再度 floor する**。
 * 1回の掛け算にまとめると（= base をfloorせずにratioを掛けてから1回だけfloorすると）1pxずれる
 * 比率があるため、必ずこの順序を守ること（backend B5実行メモの指示どおり）。
 */
export function computeCaptionFontSize(width: number, height: number, size: TelopSize): number {
  const base = Math.max(
    12,
    Math.floor(
      Math.min((width * CAPTION_WIDTH_BUDGET) / CAPTION_MAX_CHARS_PER_LINE, height * CAPTION_HEIGHT_BUDGET)
    )
  );
  return Math.max(12, Math.floor(base * CAPTION_SIZE_RATIO[size]));
}

export const CAPTION_SIZE_OPTIONS: { key: TelopSize; label: string }[] = [
  { key: 'sm', label: '小' },
  { key: 'md', label: '中' },
  { key: 'lg', label: '大' },
];
